import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const PanelContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 40px;
  background: rgba(0, 0, 0, 0.95);
  color: #00ff00;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  z-index: 1000;
  font-family: 'Ubuntu', sans-serif;
  border-bottom: 1px solid rgba(0, 255, 0, 0.3);
  backdrop-filter: blur(10px);
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Logo = styled.div`
  font-weight: bold;
  color: #00ff00;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PerformanceMonitor = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  font-size: 11px;
  color: #cccccc;
`;

const MetricItem = styled.div<{ usage: number }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => 
    props.usage > 80 ? '#ff4444' : 
    props.usage > 60 ? '#ffaa00' : 
    '#00ff00'
  };
`;

const UsageBar = styled.div<{ usage: number }>`
  width: 30px;
  height: 4px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 2px;
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    width: ${props => props.usage}%;
    height: 100%;
    background: ${props => 
      props.usage > 80 ? '#ff4444' : 
      props.usage > 60 ? '#ffaa00' : 
      '#00ff00'
    };
    transition: width 0.5s ease;
  }
`;

const StatusIndicator = styled.div<{ status: 'online' | 'secure' | 'active' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
  
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${props => 
      props.status === 'online' ? '#00ff00' :
      props.status === 'secure' ? '#ffff00' : '#ff0000'
    };
  }
`;

const BatteryIndicator = styled.div<{ level: number; charging: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  color: ${props => 
    props.level > 50 ? '#00ff00' : 
    props.level > 20 ? '#ffaa00' : 
    '#ff4444'
  };
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
`;

const BatteryIcon = styled.div<{ level: number; charging: boolean }>`
  width: 20px;
  height: 10px;
  border: 1px solid currentColor;
  border-radius: 1px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    right: -3px;
    width: 2px;
    height: 4px;
    background: currentColor;
    border-radius: 0 1px 1px 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    width: ${props => (props.level / 100) * 16}px;
    height: 6px;
    background: currentColor;
    border-radius: 1px;
    transition: width 0.5s ease;
    ${props => props.charging && `
      animation: charging 1s ease-in-out infinite alternate;
    `}
  }
  
  @keyframes charging {
    0% { opacity: 0.5; }
    100% { opacity: 1; }
  }
`;

const DateTimeDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
`;

const TimeText = styled.div`
  font-size: 12px;
  font-weight: bold;
  color: #00ff00;
`;

const DateText = styled.div`
  font-size: 9px;
  color: #cccccc;
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 12px;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
`;

const NotificationIcon = styled.div<{ hasNotifications: boolean }>`
  position: relative;
  cursor: pointer;
  padding: 4px;
  border-radius: 3px;
  font-size: 14px;
  transition: background 0.3s ease;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
  
  ${props => props.hasNotifications && `
    &::after {
      content: '';
      position: absolute;
      top: 2px;
      right: 2px;
      width: 6px;
      height: 6px;
      background: #ff4444;
      border-radius: 50%;
    }
  `}
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
`;

const CalendarPopup = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 45px;
  right: 100px;
  background: rgba(0, 0, 0, 0.95);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
  z-index: 2000;
  display: ${props => props.visible ? 'block' : 'none'};
  backdrop-filter: blur(10px);
  min-width: 250px;
`;

const CalendarHeader = styled.div`
  color: #00ffff;
  font-weight: bold;
  margin-bottom: 10px;
  text-align: center;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  font-size: 10px;
`;

const CalendarDay = styled.div<{ isToday: boolean }>`
  padding: 4px;
  text-align: center;
  color: ${props => props.isToday ? '#00ff00' : '#cccccc'};
  background: ${props => props.isToday ? 'rgba(0, 255, 0, 0.2)' : 'transparent'};
  border-radius: 2px;
  cursor: pointer;
  
  &:hover {
    background: rgba(0, 255, 0, 0.1);
  }
`;

const TopPanel: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [uptime, setUptime] = useState(0);
  const [cpuUsage, setCpuUsage] = useState(15);
  const [ramUsage, setRamUsage] = useState(45);
  const [batteryLevel, setBatteryLevel] = useState(87);
  const [isCharging, setIsCharging] = useState(false);
  const [volume, setVolume] = useState(75);
  const [showCalendar, setShowCalendar] = useState(false);
  const [hasNotifications, setHasNotifications] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
      setUptime(prev => prev + 1);
      
      // Simulate performance metrics
      setCpuUsage(prev => Math.max(5, Math.min(95, prev + (Math.random() - 0.5) * 10)));
      setRamUsage(prev => Math.max(20, Math.min(90, prev + (Math.random() - 0.5) * 5)));
      
      // Simulate battery drain/charge
      if (Math.random() > 0.98) {
        setIsCharging(prev => !prev);
      }
      if (Math.random() > 0.95) {
        setBatteryLevel(prev => 
          isCharging ? Math.min(100, prev + 1) : Math.max(0, prev - 1)
        );
      }
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isCharging]);

  const formatUptime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getVolumeIcon = () => {
    if (volume === 0) return '🔇';
    if (volume < 30) return '🔈';
    if (volume < 70) return '🔉';
    return '🔊';
  };

  const renderCalendar = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Add day headers
    dayNames.forEach(day => {
      days.push(
        <CalendarDay key={day} isToday={false} style={{ fontWeight: 'bold', color: '#00ffff' }}>
          {day}
        </CalendarDay>
      );
    });
    
    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<CalendarDay key={`empty-${i}`} isToday={false}></CalendarDay>);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = day === today.getDate();
      days.push(
        <CalendarDay key={day} isToday={isToday}>
          {day}
        </CalendarDay>
      );
    }
    
    return days;
  };

  return (
    <>
      <PanelContainer>
        <LeftSection>
          <Logo>
            🐧 Mukul's Portfolio
          </Logo>
          
          <PerformanceMonitor>
            <MetricItem usage={cpuUsage}>
              <span>CPU</span>
              <UsageBar usage={cpuUsage} />
              <span>{Math.round(cpuUsage)}%</span>
            </MetricItem>
            
            <MetricItem usage={ramUsage}>
              <span>RAM</span>
              <UsageBar usage={ramUsage} />
              <span>{Math.round(ramUsage)}%</span>
            </MetricItem>
          </PerformanceMonitor>
        </LeftSection>

        <RightSection>
          <StatusIndicator status="secure">Secure</StatusIndicator>
          <StatusIndicator status="online">Online</StatusIndicator>
          
          <VolumeControl>
            {getVolumeIcon()}
            <span>{volume}%</span>
          </VolumeControl>
          
          <BatteryIndicator level={batteryLevel} charging={isCharging}>
            <BatteryIcon level={batteryLevel} charging={isCharging} />
            <span>{batteryLevel}%</span>
            {isCharging && <span>⚡</span>}
          </BatteryIndicator>
          
          <NotificationIcon hasNotifications={hasNotifications}>
            🔔
          </NotificationIcon>
          
          <DateTimeDisplay onClick={() => setShowCalendar(!showCalendar)}>
            <TimeText>{currentTime.toLocaleTimeString()}</TimeText>
            <DateText>{currentTime.toLocaleDateString()}</DateText>
          </DateTimeDisplay>
          
          <UserMenu>
            👤 mukul
          </UserMenu>
        </RightSection>
      </PanelContainer>

      <CalendarPopup visible={showCalendar}>
        <CalendarHeader>
          {currentTime.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </CalendarHeader>
        <CalendarGrid>
          {renderCalendar()}
        </CalendarGrid>
      </CalendarPopup>
    </>
  );
};

export default TopPanel;
